import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    Link2, 
    Copy, 
    QrCode, 
    RefreshCw, 
    Loader2, 
    Users,
    Download,
    CheckCircle2,
    ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InviteLinkCard = ({ className = '' }) => {
    const { token, user } = useAuth();
    const { tenant } = useTenant();
    
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (user?.role === 'org_admin' || user?.role === 'sales_rep') {
            fetchInvite();
        } else {
            setLoading(false);
        }
    }, [user]);

    const fetchInvite = async () => {
        try {
            const response = await axios.get(`${API}/user-invites/my-invite`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvite(response.data);
        } catch (error) {
            console.error('Failed to fetch invite:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!invite?.invite_url) return;
        
        try {
            await navigator.clipboard.writeText(invite.invite_url);
            setCopied(true);
            toast.success('Link kopiert!');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error('Kopieren fehlgeschlagen');
        }
    };

    const handleRegenerate = async () => {
        if (!window.confirm('Möchten Sie wirklich einen neuen Einladungslink generieren? Der alte Link wird ungültig.')) {
            return;
        }
        
        setRegenerating(true);
        try {
            const response = await axios.post(
                `${API}/user-invites/regenerate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInvite(response.data);
            toast.success('Neuer Link generiert');
        } catch (error) {
            toast.error('Fehler beim Generieren des Links');
        } finally {
            setRegenerating(false);
        }
    };

    const handleDownloadQR = async () => {
        if (!invite?.code) return;
        
        try {
            const response = await axios.get(
                `${API}/user-invites/qr/${invite.code}?size=400`,
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `einladung-qr-${invite.code.slice(0, 8)}.png`;
            link.click();
            window.URL.revokeObjectURL(url);
            
            toast.success('QR-Code heruntergeladen');
        } catch (error) {
            toast.error('Download fehlgeschlagen');
        }
    };

    // Don't show for customers
    if (!user || (user.role !== 'org_admin' && user.role !== 'sales_rep')) {
        return null;
    }

    if (loading) {
        return (
            <Card className={className}>
                <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </CardContent>
            </Card>
        );
    }

    if (!invite) {
        return (
            <Card className={className}>
                <CardContent className="p-6 text-center text-slate-500">
                    Einladungslink nicht verfügbar
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className={className} data-testid="invite-link-card">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                            >
                                <Link2 size={20} style={{ color: tenant?.primary_color }} />
                            </div>
                            <div>
                                <CardTitle className="text-lg">Ihr Einladungslink</CardTitle>
                                <CardDescription>
                                    Teilen Sie diesen Link mit potenziellen Tippgebern
                                </CardDescription>
                            </div>
                        </div>
                        <Badge variant="outline" className="gap-1">
                            <Users size={12} />
                            {invite.referrer_count} Registrierungen
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Invite Link */}
                    <div className="flex gap-2">
                        <Input
                            value={invite.invite_url}
                            readOnly
                            className="font-mono text-sm bg-slate-50"
                            data-testid="invite-url-input"
                        />
                        <Button
                            variant={copied ? "default" : "outline"}
                            size="icon"
                            onClick={handleCopyLink}
                            className={copied ? "bg-green-500 hover:bg-green-600" : ""}
                            data-testid="copy-link-btn"
                        >
                            {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                        </Button>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setQrDialogOpen(true)}
                            data-testid="show-qr-btn"
                        >
                            <QrCode size={16} className="mr-2" />
                            QR-Code anzeigen
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(invite.invite_url, '_blank')}
                        >
                            <ExternalLink size={16} className="mr-2" />
                            Link testen
                        </Button>
                        {user.role === 'org_admin' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleRegenerate}
                                disabled={regenerating}
                                className="text-slate-500"
                            >
                                {regenerating ? (
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                ) : (
                                    <RefreshCw size={16} className="mr-2" />
                                )}
                                Neuen Link generieren
                            </Button>
                        )}
                    </div>
                    
                    <p className="text-xs text-slate-500">
                        Personen, die sich über diesen Link registrieren, werden automatisch Ihnen zugewiesen.
                    </p>
                </CardContent>
            </Card>

            {/* QR Code Dialog */}
            <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>QR-Code für Einladung</DialogTitle>
                        <DialogDescription>
                            Scannen Sie diesen Code, um direkt zur Registrierung zu gelangen
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-4">
                        <div className="bg-white p-4 rounded-xl shadow-lg">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/user-invites/qr/${invite.code}?size=300`}
                                alt="QR-Code"
                                className="w-[300px] h-[300px]"
                                data-testid="qr-code-image"
                            />
                        </div>
                        <Button onClick={handleDownloadQR} className="w-full" data-testid="download-qr-btn">
                            <Download size={18} className="mr-2" />
                            QR-Code herunterladen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
